import React, { useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { Formik, Field, ErrorMessage, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { presetsAllAction } from '../../Redux/Actions/presets';
import { createEventPackage, eventPackagesListAction, updateEventPackage } from '../../Redux/Actions/eventPackages';
import { EventTypeMultiSelect } from './CreateEventModal';

const AddEventPackageModal = ({ show, handleClose, packageData }) => {
    const dispatch = useDispatch();
    const allPresets = useSelector((e) => e.presetAuth.presetAllList);

    useEffect(() => {
        if (show) dispatch(presetsAllAction());
    }, [show, dispatch]);

    const presetName = (id) => allPresets?.find((p) => p._id === id)?.eventType || '';

    const initialValues = packageData?._id ? {
        name: packageData?.name || '',
        presets: packageData?.presets?.map((p) => p?._id || p) || [],
    } : {
        name: '',
        presets: [],
    };

    const validationSchema = Yup.object({
        name: Yup.string().trim().required('Package name is required'),
        presets: Yup.array().min(2, 'Select at least 2 event presets'),
    });

    const handleSubmit = (values) => {
        const payload = { name: values.name.trim(), presets: values.presets };
        const action = packageData?._id
            ? updateEventPackage({ ...payload, packageId: packageData._id })
            : createEventPackage(payload);

        dispatch(action).then((res) => {
            if (res?.payload?.statusCode === 200) {
                toast.success(`Package ${packageData?._id ? 'Updated' : 'Created'} Successfully`);
                dispatch(eventPackagesListAction({ page: 0, limit: 100 }));
                handleClose();
            } else {
                toast.error(res?.payload?.message);
            }
        });
    };

    return (
        <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false}>
            <Modal.Header className="border-none justify-content-center">
                <Modal.Title className="mt-3">{packageData?._id ? 'Edit' : 'Create'} Event Package</Modal.Title>
            </Modal.Header>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, setFieldValue, setFieldTouched, touched, errors }) => {
                    // Keep the existing order for presets that stay selected, append new ones at the end
                    const handlePresetsChange = (selected) => {
                        const kept = values.presets.filter((id) => selected.includes(id));
                        const added = selected.filter((id) => !kept.includes(id));
                        setFieldValue('presets', [...kept, ...added]);
                    };

                    const handleDragEnd = (result) => {
                        if (!result.destination) return;
                        const reordered = [...values.presets];
                        const [moved] = reordered.splice(result.source.index, 1);
                        reordered.splice(result.destination.index, 0, moved);
                        setFieldValue('presets', reordered);
                    };

                    return (
                        <FormikForm>
                            <Modal.Body className="py-0">
                                <Form.Group className="mb-3">
                                    <Form.Label>Package Name</Form.Label>
                                    <Field
                                        type="text"
                                        name="name"
                                        className="form-control"
                                        placeholder="e.g. Leggero Package"
                                    />
                                    <ErrorMessage name="name" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Event Presets</Form.Label>
                                    <EventTypeMultiSelect
                                        options={allPresets?.map((p) => ({ value: p._id, label: p.eventType })) || []}
                                        value={values.presets}
                                        onConfirm={handlePresetsChange}
                                        onBlur={() => setFieldTouched('presets', true, false)}
                                        isInvalid={!!(touched.presets && errors.presets)}
                                        placeholder="Select Event Presets"
                                    />
                                    {touched.presets && errors.presets && (
                                        <div className="text-danger">{errors.presets}</div>
                                    )}
                                </Form.Group>

                                {values.presets.length > 1 && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Order on client side (drag to reorder)</Form.Label>
                                        <DragDropContext onDragEnd={handleDragEnd}>
                                            <Droppable droppableId="package-presets">
                                                {(provided) => (
                                                    <div {...provided.droppableProps} ref={provided.innerRef}>
                                                        {values.presets.map((id, index) => (
                                                            <Draggable key={id} draggableId={id} index={index}>
                                                                {(provided) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        style={{
                                                                            ...provided.draggableProps.style,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '10px',
                                                                            padding: '8px 12px',
                                                                            marginBottom: '6px',
                                                                            border: '1px solid #e5e5e5',
                                                                            borderRadius: '8px',
                                                                            background: '#fff',
                                                                            cursor: 'move',
                                                                        }}
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                            <line x1="3" y1="12" x2="21" y2="12"></line>
                                                                            <line x1="3" y1="6" x2="21" y2="6"></line>
                                                                            <line x1="3" y1="18" x2="21" y2="18"></line>
                                                                        </svg>
                                                                        <span>{index + 1}. {presetName(id)}</span>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </DragDropContext>
                                    </Form.Group>
                                )}
                            </Modal.Body>
                            <Modal.Footer className="border-none justify-content-center">
                                <button className="secondary" type="button" onClick={handleClose}>
                                    Close
                                </button>
                                <button className="primary" type="submit">
                                    {packageData?._id ? 'Update' : 'Create'}
                                </button>
                            </Modal.Footer>
                        </FormikForm>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default AddEventPackageModal;
